import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import type { Quote_ } from 'src/api/db/apiQuote'
import { useQuoteApi } from 'src/api/db/apiQuote'
import { useSendEmail } from 'src/api/server/useSendEmail'
import { FormInput, FormTextarea, FormWrapper } from 'src/shared/components/FormComponents'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { useRequestContext } from '../../../hooks/RequestContext'
import {
  buildDefaultInvoiceData,
  formatDocumentDate,
  getBillingDocumentData,
} from '../utils'

const formSchema = z.object({
  toEmail: z.string().email('Invalid email'),
  subject: z.string().min(1),
  message: z.string().min(1),
})

type FormValues = z.infer<typeof formSchema>

const buildDefaultMessage = (quote: Quote_) => {
  const invoice = getBillingDocumentData(quote)
  return [
    `Hej${invoice.buyer.name ? ` ${invoice.buyer.name}` : ''},`,
    '',
    `Här kommer faktura ${invoice.invoiceNumber}.`,
    `Fakturadatum: ${formatDocumentDate(invoice.issueDate)}`,
    `Förfallodatum: ${formatDocumentDate(invoice.dueDate)}`,
    invoice.paymentReference
      ? `Betalreferens: ${invoice.paymentReference}`
      : null,
    '',
    'Med vänliga hälsningar',
    invoice.seller.name,
  ]
    .filter(Boolean)
    .join('\n')
}

export const SendInvoiceDialog = ({
  quote,
  open,
  onOpenChange,
}: {
  quote: Quote_
  open: boolean
  onOpenChange: (open: boolean) => void
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { refetchQuote } = useRequestContext()
  const { updateQuote } = useQuoteApi({ skipListQuery: true })
  const sendEmail = useSendEmail()

  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      toEmail: '',
      subject: '',
      message: '',
    },
  })

  useEffect(() => {
    if (!open) return
    const invoice = getBillingDocumentData(quote)
    methods.reset({
      toEmail: invoice.buyer.email || '',
      subject: `Faktura ${invoice.invoiceNumber}`,
      message: buildDefaultMessage(quote),
    })
  }, [open, quote, methods])

  const handleSubmit = async (values: FormValues) => {
    const current = getBillingDocumentData(quote)
    const { isDraftPreview: _, ...savedFields } = current
    const base = current.isDraftPreview
      ? buildDefaultInvoiceData(quote)
      : savedFields
    const invoiceData = {
      ...base,
      buyer: { ...base.buyer, email: values.toEmail },
      status: 'sent' as const,
      updatedAt: new Date().toISOString(),
    }

    try {
      await sendEmail.mutateAsync({
        toEmails: [values.toEmail],
        subject: values.subject,
        message: values.message,
      })

      const result = await updateQuote.mutateAsync({
        id: quote.id,
        invoice_data: invoiceData,
      })
      if (result.error) throw result.error

      refetchQuote()
      toast({
        title: t('Invoice sent', 'Faktura skickad'),
        description: values.toEmail,
      })
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to send invoice', err)
      toast({
        variant: 'destructive',
        title: t('Could not send invoice', 'Kunde inte skicka faktura'),
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }

  const isPending = sendEmail.isPending || updateQuote.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Send invoice', 'Skicka faktura')}</DialogTitle>
        </DialogHeader>

        <FormWrapper methods={methods} onSubmit={handleSubmit}>
          <Column className="gap-4">
            <FormInput
              label={t('To', 'Till')}
              name="toEmail"
              methods={methods}
              type="email"
              className="w-full"
            />
            <FormInput
              label={t('Subject', 'Ämne')}
              name="subject"
              methods={methods}
              className="w-full"
            />
            <FormTextarea
              label={t('Message', 'Meddelande')}
              name="message"
              methods={methods}
              className="w-full"
              rows={8}
            />
            <Row className="justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
              >
                {t('Cancel', 'Avbryt')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? t('Sending...', 'Skickar...')
                  : t('Send invoice', 'Skicka faktura')}
              </Button>
            </Row>
          </Column>
        </FormWrapper>
      </DialogContent>
    </Dialog>
  )
}
